/**
 * Workspace plans, features & quotas — the single source of truth for "what may
 * this workspace do and how much may it use", shared by the API (enforcement)
 * and the web (display) so the two can never drift.
 *
 * A workspace (Org) has a `plan` (`basic | pro`). The plan is a PRESET: it sets
 * each feature's default and the quota defaults. An operator may override any
 * single feature per workspace (Org.featureOverrides JSON: key → boolean; a
 * missing key inherits the plan) and any quota (the nullable *Override columns).
 *
 * Quota override columns use a sentinel so one nullable column has three states:
 *   - `null`      → inherit the plan default
 *   - `< 0` (-1)  → explicitly UNLIMITED
 *   - `>= 0`      → that exact limit
 * A resolved quota of `null` means "unlimited".
 *
 * Adding a feature = one FEATURES entry + its enforcement point. No migration.
 */

export type OrgPlan = 'basic' | 'pro';
export const ORG_PLANS: readonly OrgPlan[] = ['basic', 'pro'];

/**
 * Where an agent run's Claude + GitHub credentials come from — derived from the
 * `byoCredentials` feature:
 *   - `shared` → the platform's own tokens. The workspace never configures GitHub.
 *   - `byo`    → the workspace's own tokens (per-org OrgSetting).
 */
export type CredentialMode = 'shared' | 'byo';
export const CREDENTIAL_MODES: readonly CredentialMode[] = ['shared', 'byo'];

export type FeatureKey =
  | 'integrationTokens'
  | 'workspacePrototypeDomain'
  | 'prototypeCustomDomains'
  | 'mcpTokens'
  | 'repoProjects'
  | 'byoCredentials'
  | 'resourceLimitOverrides'
  | 'chat'
  | 'loops';

/** Display order (ops Access tab, tenant "Included features", feature squares). */
export const FEATURE_KEYS: readonly FeatureKey[] = [
  'integrationTokens',
  'workspacePrototypeDomain',
  'prototypeCustomDomains',
  'mcpTokens',
  'repoProjects',
  'byoCredentials',
  'resourceLimitOverrides',
  'chat',
  'loops',
];

export interface FeatureDef {
  label: string;
  /** One plain sentence, reused by the ops Access tab and the tenant locked state. */
  description: string;
  defaults: Record<OrgPlan, boolean>;
}

export const FEATURES: Record<FeatureKey, FeatureDef> = {
  integrationTokens: {
    label: 'Integration API tokens',
    description: 'Mint Integration API tokens that let an external server create prototypes and submit requests.',
    defaults: { basic: false, pro: false },
  },
  workspacePrototypeDomain: {
    label: 'Workspace prototype domain',
    description: "Serve every prototype under the workspace's own wildcard domain.",
    defaults: { basic: false, pro: false },
  },
  prototypeCustomDomains: {
    label: 'Prototype custom domains',
    description: 'Attach a custom hostname (CNAME) to an individual prototype.',
    defaults: { basic: false, pro: true },
  },
  mcpTokens: {
    label: 'MCP',
    description: 'Connect MCP clients like Claude Code (browser sign-in, OAuth) so they can read this workspace through the observability endpoint.',
    defaults: { basic: true, pro: true },
  },
  repoProjects: {
    label: 'Repo projects',
    description: 'Connect GitHub repositories as projects, not only prototypes.',
    defaults: { basic: false, pro: true },
  },
  byoCredentials: {
    label: 'Bring your own credentials',
    description: "Run agents on the workspace's own Claude and GitHub tokens instead of the platform's.",
    defaults: { basic: false, pro: true },
  },
  resourceLimitOverrides: {
    label: 'Prototype resource limits',
    description: "Raise or lower a prototype's memory and CPU limits.",
    defaults: { basic: false, pro: true },
  },
  chat: {
    label: 'PM chat',
    description: 'Talk to the Product Manager agent in the project chat.',
    defaults: { basic: true, pro: true },
  },
  loops: {
    label: 'Loops',
    description: 'Schedule recurring agent missions on a cron schedule.',
    defaults: { basic: false, pro: true },
  },
};

/** The stored per-workspace overrides (a missing key inherits the plan). */
export type FeatureOverrides = Partial<Record<FeatureKey, boolean>>;
/** An operator edit: true/false sets an override, null clears it. */
export type FeatureOverridePatch = Partial<Record<FeatureKey, boolean | null>>;

export interface ResolvedFeature {
  enabled: boolean;
  source: 'plan' | 'override';
}
export type ResolvedFeatures = Record<FeatureKey, ResolvedFeature>;

export const FEATURE_DISABLED_CODE = 'FEATURE_DISABLED';
/** 403 `code` when the workspace is at its prototype cap (body also carries `count` / `max` / `plan`). */
export const PROTOTYPE_LIMIT_REACHED_CODE = 'PROTOTYPE_LIMIT_REACHED';

export function isFeatureKey(k: string): k is FeatureKey {
  return Object.prototype.hasOwnProperty.call(FEATURES, k);
}

/** Read the JSON column defensively: unknown keys and non-booleans are ignored. */
export function parseFeatureOverrides(raw: unknown): FeatureOverrides {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: FeatureOverrides = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (isFeatureKey(k) && typeof v === 'boolean') out[k] = v;
  }
  return out;
}

/** Apply an operator patch (null deletes the key) without mutating `current`. */
export function applyFeatureOverridePatch(
  current: FeatureOverrides,
  patch: FeatureOverridePatch,
): FeatureOverrides {
  const next: FeatureOverrides = { ...current };
  for (const k of FEATURE_KEYS) {
    if (!(k in patch)) continue;
    const v = patch[k];
    if (v === null) delete next[k];
    else if (typeof v === 'boolean') next[k] = v;
  }
  return next;
}

export function countFeatureOverrides(raw: unknown): number {
  return Object.keys(parseFeatureOverrides(raw)).length;
}

export function featureDisabledMessage(key: FeatureKey): string {
  return `${FEATURES[key].label} isn't included in this workspace. Contact your platform operator to enable it.`;
}

/** Quota defaults per plan (features live in FEATURES). `null` = unlimited. */
export const PLAN_QUOTA_DEFAULTS: Record<
  OrgPlan,
  { maxPrototypes: number | null; monthlySpendCapUsd: number | null }
> = {
  basic: { maxPrototypes: 3, monthlySpendCapUsd: 20 },
  pro: { maxPrototypes: null, monthlySpendCapUsd: null },
};

/** The resolved, effective entitlements for a workspace. `null` quota = unlimited. */
export interface Entitlements {
  plan: OrgPlan;
  features: ResolvedFeatures;
  /** Derived: features.byoCredentials.enabled ? 'byo' : 'shared'. */
  credentialMode: CredentialMode;
  /** Max prototypes the workspace may create. `null` = unlimited. */
  maxPrototypes: number | null;
  /** Rolling calendar-month spend cap in USD. `null` = unlimited. */
  monthlySpendCapUsd: number | null;
}

/** The raw per-workspace overrides as stored on Org. */
export interface EntitlementOverrides {
  /** Org.featureOverrides (JSON) — parsed defensively. */
  featureOverrides?: unknown;
  /** Quota sentinel: null = inherit, < 0 = unlimited, >= 0 = the limit. */
  maxPrototypes?: number | null;
  /** Quota sentinel: null = inherit, < 0 = unlimited, >= 0 = the limit. */
  monthlySpendCapUsd?: number | null;
}

function resolveQuota(
  override: number | null | undefined,
  planDefault: number | null,
): number | null {
  if (override == null) return planDefault;
  if (override < 0) return null;
  return override;
}

/**
 * The single resolution path: each feature is `override ?? planDefault`, each
 * quota follows the sentinel. Pure — the same function runs on the server
 * (enforcement) and in the browser (display).
 */
export function resolveEntitlements(
  plan: OrgPlan,
  overrides: EntitlementOverrides = {},
): Entitlements {
  const effectivePlan: OrgPlan = plan === 'pro' ? 'pro' : 'basic';
  const ov = parseFeatureOverrides(overrides.featureOverrides);
  const features = {} as ResolvedFeatures;
  for (const k of FEATURE_KEYS) {
    const o = ov[k];
    features[k] =
      o === undefined
        ? { enabled: FEATURES[k].defaults[effectivePlan], source: 'plan' }
        : { enabled: o, source: 'override' };
  }
  const q = PLAN_QUOTA_DEFAULTS[effectivePlan];
  return {
    plan,
    features,
    credentialMode: features.byoCredentials.enabled ? 'byo' : 'shared',
    maxPrototypes: resolveQuota(overrides.maxPrototypes, q.maxPrototypes),
    monthlySpendCapUsd: resolveQuota(overrides.monthlySpendCapUsd, q.monthlySpendCapUsd),
  };
}

/** Whether `usage` has reached/exceeded `limit`. A `null` (unlimited) limit is never exceeded. */
export function isQuotaExceeded(usage: number, limit: number | null): boolean {
  if (limit == null) return false;
  return usage >= limit;
}

/** The UTC start-of-month boundary for `now` — the reset point for the monthly spend window. */
export function monthStart(now: Date): Date {
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
}

/** The next UTC start-of-month after `now` — when the monthly spend allowance resets. */
export function nextMonthStart(now: Date): Date {
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));
}

/** Current usage of a workspace's metered quotas, for the plan/usage UI. */
export interface EntitlementUsage {
  prototypeCount: number;
  monthToDateSpendUsd: number;
  /** ISO timestamp when the monthly spend window next resets. */
  periodResetsAt: string;
}

/** Effective entitlements + live usage for a workspace — the plan/usage API payload. */
export interface WorkspaceEntitlementsDto extends Entitlements {
  usage: EntitlementUsage;
}
